import type { Location } from "history";
import { match } from "ts-pattern";
import { t } from "ttag";

import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { PageContainer } from "metabase/data-studio/common/components/PageContainer";
import * as Urls from "metabase/lib/urls";
import { TransformDisconnectedDatabaseBanner } from "metabase/transforms/components/TransformDisconnectedDatabaseBanner";
import { TransformHeader } from "metabase/transforms/components/TransformHeader";
import { useTransformPermissions } from "metabase/transforms/hooks/use-transform-permissions";
import { useTransformWithPolling } from "metabase/transforms/hooks/use-transform-with-polling";
import { RunSection } from "metabase/transforms/pages/TransformRunPage/RunSection";
import { isMissingSourceDatabase } from "metabase/transforms/utils";
import { Alert, Center, Icon, Text } from "metabase/ui";

import { InspectorContent } from "./components/InspectorContent";
import type { RouteParams } from "./types";

type TransformInspectPageProps = {
  params: RouteParams;
  location: Location;
};

export const TransformInspectPage = ({
  params,
  location,
}: TransformInspectPageProps) => {
  const transformId = Urls.extractEntityId(params.transformId);
  const {
    transform,
    isLoading: isLoadingTransform,
    error: transformError,
  } = useTransformWithPolling(transformId);
  const { readOnly, isLoadingDatabases, databasesError } =
    useTransformPermissions({ transform });

  const isLoading = isLoadingTransform || isLoadingDatabases;
  const error = transformError || databasesError;

  if (isLoading || error || !transform) {
    return (
      <Center h="100%">
        <LoadingAndErrorWrapper loading={isLoading} error={error} />
      </Center>
    );
  }

  return (
    <PageContainer data-testid="transform-inspect-content">
      <TransformHeader transform={transform} />
      {match({
        hasSucceeded: transform.last_run?.status === "succeeded",
        isMissingSourceDatabase: isMissingSourceDatabase(transform),
      })
        .with({ isMissingSourceDatabase: true }, () => (
          <TransformDisconnectedDatabaseBanner transform={transform} />
        ))
        .with({ hasSucceeded: false }, () => (
          <>
            <Alert color="brand" icon={<Icon name="info" />}>
              <Text>{t`To inspect the transform you need to run it first.`}</Text>
            </Alert>
            <RunSection
              transform={transform}
              noTitle={true}
              readOnly={readOnly}
            />
          </>
        ))
        .otherwise(() => (
          <InspectorContent
            transform={transform}
            params={params}
            location={location}
          />
        ))}
    </PageContainer>
  );
};
